// Meu acervo de bancos — File Explorer estilo Windows.
//
// Mistura tree de pastas (esquerda) + grid de bancos (direita), com subpastas,
// drag-and-drop cross-pane (banco -> pasta, pasta -> pasta), undo/redo, visões
// múltiplas e ações por contexto. Substitui o flat-chip toolbar antigo dentro
// da seção "Meu acervo de bancos".
//
// Decisões:
//   - Tree client-side derivada de foldersData (flat com parentId). Sem novo
//     endpoint; backend já suporta subpastas via bankFolders.parentId.
//   - DnD com dnd-kit (já é dependência) usando prefixos:
//       drag:  "bank:<id>"  ou "folder:<id>"
//       drop:  "tgt:<folderId | 'root' | 'no-folder'>"
//   - Cycle-detection: ao reparenting pasta, verifica que o target não é
//     descendente da pasta movida.
//   - Undo/Redo: stack de movimentos (bank-move + folder-reparent). Ctrl+Z /
//     Ctrl+Y aplicam inverso/forward. Bar inferior mostra o histórico.
//   - Visão "raiz": quando seleção === null mostra tiles de pastas + bancos
//     sem pasta. Quando "no-folder" só sem-pasta. Quando id de pasta, só os
//     bancos diretos dessa pasta.
//   - Counts: bankCount no chip da pasta soma descendentes; "no-folder" e
//     "raiz" não contam descendentes (raiz é especial).

import { signal, computed } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import {
  Folder,
  FolderOpen,
  FolderPlus,
  FolderClosed,
  ChevronRight,
  ChevronDown,
  LayoutGrid,
  LayoutList,
  List,
  Search,
  X,
  Undo2,
  Redo2,
  History,
  Sparkles,
  MoreVertical,
  Edit3,
  Trash2,
  Loader2,
  Home,
  Package,
  GripVertical,
  Move
} from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent
} from "@dnd-kit/core";
import { call, mergeBanks } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { Checkbox } from "@app/ui/components/checkbox";
import { Layers, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";
import {
  studyGroupsList,
  studyGroupsPhase,
  loadStudyGroups,
  openStudyGroup
} from "./StudyGroups";
import { setActiveNav } from "./App";
import { Users as UsersIcon, ChevronRight as ChevronRightIcon } from "lucide-react";
import { openAlert, openConfirm } from "./ConfirmPromptModal";
import {
  foldersData,
  foldersPhase,
  loadFolders,
  bankSearchQuery,
  bankViewMode,
  setBankViewMode,
  type FolderRow,
  type BankViewMode
} from "./BankFolders";
import { BankCard, loadBanks, banksData, setActiveNav } from "./App";
import { openBank } from "./BankDetailView";
import type { BankRow } from "./api";
import {
  ContextMenuOverlay,
  openContextMenu,
  openContextMenuAt,
  type ContextMenuEntry
} from "./ContextMenu";
import { useLongPress } from "./useLongPress";
import { OrganizeToggle } from "./OrganizeToggle";
import {
  FolderInput,
  Share2,
  Eye,
  ExternalLink
} from "lucide-react";

// =====================================================================
// Tipos / Signals
// =====================================================================

type Selection = string | null | "no-folder";

const STORAGE_SELECTION = "student-acervo-selection-v1";
const STORAGE_EXPANDED = "student-acervo-expanded-v1";

function readSelection(): Selection {
  try {
    const v = localStorage.getItem(STORAGE_SELECTION);
    if (v === null) return null;
    if (v === "no-folder" || v === "__root__") return v === "__root__" ? null : "no-folder";
    return v;
  } catch {
    return null;
  }
}
function writeSelection(s: Selection) {
  try {
    localStorage.setItem(STORAGE_SELECTION, s === null ? "__root__" : s);
  } catch {
    /* ignore */
  }
}
function readExpanded(): Set<string> {
  try {
    const v = localStorage.getItem(STORAGE_EXPANDED);
    if (!v) return new Set();
    return new Set(JSON.parse(v) as string[]);
  } catch {
    return new Set();
  }
}
function writeExpanded(s: Set<string>) {
  try {
    localStorage.setItem(STORAGE_EXPANDED, JSON.stringify(Array.from(s)));
  } catch {
    /* ignore */
  }
}

export const explorerSelection = signal<Selection>(readSelection());
export const explorerExpanded = signal<Set<string>>(readExpanded());

// Modo organizar: arrasto de bancos/pastas só fica ativo quando ligado. Default
// desligado (e NÃO persiste) pra tela abrir "segura" — no mobile o usuário
// deslizava e arrastava sem querer. [[feedback Luís 2026-05-27]]
export const acervoEditMode = signal(false);

// Largura da sidebar de pastas — persistida + clamp 200..520px.
const STORAGE_SIDEBAR_WIDTH = "student-acervo-sidebar-w-v1";
const SIDEBAR_W_MIN = 200;
const SIDEBAR_W_MAX = 520;
const SIDEBAR_W_DEFAULT = 288; // = lg:w-72 anterior

function readSidebarWidth(): number {
  try {
    const v = localStorage.getItem(STORAGE_SIDEBAR_WIDTH);
    if (!v) return SIDEBAR_W_DEFAULT;
    const n = Number(v);
    if (!Number.isFinite(n)) return SIDEBAR_W_DEFAULT;
    return Math.max(SIDEBAR_W_MIN, Math.min(SIDEBAR_W_MAX, n));
  } catch {
    return SIDEBAR_W_DEFAULT;
  }
}
function writeSidebarWidth(w: number) {
  try {
    localStorage.setItem(STORAGE_SIDEBAR_WIDTH, String(w));
  } catch {
    /* ignore */
  }
}
const sidebarWidth = signal<number>(readSidebarWidth());

function selectFolder(s: Selection) {
  explorerSelection.value = s;
  writeSelection(s);
}
function toggleExpanded(id: string) {
  const next = new Set(explorerExpanded.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  explorerExpanded.value = next;
  writeExpanded(next);
}
function expandFolder(id: string) {
  if (!explorerExpanded.value.has(id)) {
    const next = new Set(explorerExpanded.value);
    next.add(id);
    explorerExpanded.value = next;
    writeExpanded(next);
  }
}

// =====================================================================
// Undo/Redo
// =====================================================================

type UndoOp =
  | {
      kind: "bank-move";
      bankId: string;
      bankName: string;
      from: string | null;
      to: string | null;
    }
  | {
      kind: "folder-reparent";
      folderId: string;
      folderName: string;
      from: string | null;
      to: string | null;
    };

const MAX_UNDO = 30;
const undoStack = signal<UndoOp[]>([]);
const redoStack = signal<UndoOp[]>([]);
const undoBusy = signal(false);

function pushUndo(op: UndoOp) {
  const next = [...undoStack.value, op];
  if (next.length > MAX_UNDO) next.shift();
  undoStack.value = next;
  // Qualquer nova mudança limpa o redo
  if (redoStack.value.length > 0) redoStack.value = [];
}

function describeOp(op: UndoOp, folders: FolderRow[]): string {
  const folderName = (id: string | null) => {
    if (id === null) return "raiz";
    const f = folders.find((x) => x.id === id);
    return f ? f.name : "?";
  };
  if (op.kind === "bank-move") {
    return `${op.bankName}: ${folderName(op.from)} → ${folderName(op.to)}`;
  }
  return `Pasta ${op.folderName}: ${folderName(op.from)} → ${folderName(op.to)}`;
}

async function applyMove(op: UndoOp): Promise<void> {
  if (op.kind === "bank-move") {
    await call("/api/student/bank-folders?action=move", {
      method: "POST",
      body: JSON.stringify({ bankIds: [op.bankId], folderId: op.to })
    });
  } else {
    await call(`/api/student/bank-folders?id=${encodeURIComponent(op.folderId)}`, {
      method: "PATCH",
      body: JSON.stringify({ parentId: op.to })
    });
  }
}

function inverse(op: UndoOp): UndoOp {
  return { ...op, from: op.to, to: op.from } as UndoOp;
}

async function doUndo() {
  if (undoStack.value.length === 0 || undoBusy.value) return;
  const op = undoStack.value[undoStack.value.length - 1];
  undoBusy.value = true;
  try {
    const inv = inverse(op);
    await applyMove(inv);
    undoStack.value = undoStack.value.slice(0, -1);
    redoStack.value = [...redoStack.value, op];
    await Promise.all([
      loadFolders({ silent: true }),
      loadBanks({ silent: true })
    ]);
  } catch (err) {
    void openAlert({
      title: "Não consegui desfazer",
      message: friendlyError(err)
    });
  } finally {
    undoBusy.value = false;
  }
}

async function doRedo() {
  if (redoStack.value.length === 0 || undoBusy.value) return;
  const op = redoStack.value[redoStack.value.length - 1];
  undoBusy.value = true;
  try {
    await applyMove(op);
    redoStack.value = redoStack.value.slice(0, -1);
    undoStack.value = [...undoStack.value, op];
    await Promise.all([
      loadFolders({ silent: true }),
      loadBanks({ silent: true })
    ]);
  } catch (err) {
    void openAlert({
      title: "Não consegui refazer",
      message: friendlyError(err)
    });
  } finally {
    undoBusy.value = false;
  }
}

// =====================================================================
// Tree helpers
// =====================================================================

interface TreeNode extends FolderRow {
  children: TreeNode[];
  depth: number;
}

function buildTree(folders: FolderRow[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const f of folders) byId.set(f.id, { ...f, children: [], depth: 0 });
  const roots: TreeNode[] = [];
  for (const f of folders) {
    const node = byId.get(f.id)!;
    if (f.parentId && byId.has(f.parentId)) {
      const parent = byId.get(f.parentId)!;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  // Assigna depth via DFS
  function assignDepth(n: TreeNode, d: number) {
    n.depth = d;
    n.children.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
    n.children.forEach((c) => assignDepth(c, d + 1));
  }
  roots.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  roots.forEach((r) => assignDepth(r, 0));
  return roots;
}

function descendantIds(folderId: string, folders: FolderRow[]): Set<string> {
  const result = new Set<string>();
  const byParent = new Map<string | null, string[]>();
  for (const f of folders) {
    const arr = byParent.get(f.parentId) ?? [];
    arr.push(f.id);
    byParent.set(f.parentId, arr);
  }
  const queue = [folderId];
  while (queue.length) {
    const cur = queue.shift()!;
    const kids = byParent.get(cur) ?? [];
    for (const k of kids) {
      if (!result.has(k)) {
        result.add(k);
        queue.push(k);
      }
    }
  }
  return result;
}

function totalBankCount(
  folderId: string,
  banks: BankRow[],
  folders: FolderRow[]
): number {
  const desc = descendantIds(folderId, folders);
  desc.add(folderId);
  let total = 0;
  for (const b of banks) {
    if (b.folderId && desc.has(b.folderId)) total++;
  }
  return total;
}

function childFoldersOf(
  parentId: string | null,
  folders: FolderRow[]
): FolderRow[] {
  return folders
    .filter((f) => f.parentId === parentId)
    .sort(
      (a, b) => a.position - b.position || a.name.localeCompare(b.name)
    );
}

// =====================================================================
// Componente principal
// =====================================================================

export function MeuAcervoExplorer() {
  const [draggingLabel, setDraggingLabel] = useState<string | null>(null);
  const [draggingType, setDraggingType] = useState<"bank" | "folder" | null>(
    null
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  // Carrega pastas ao montar. Antes só o BankFoldersToolbar (antigo) disparava
  // loadFolders, mas o MeuAcervoExplorer não usa mais aquele toolbar — então
  // pastas apareciam só depois que o aluno criava uma nova (que chama
  // loadFolders no submit). Bug 2026-05-24. [[project_drag_drop_banks_folders]]
  useEffect(() => {
    if (foldersPhase.value === "idle") void loadFolders();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName ?? "";
      const editing =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
        (e.target as HTMLElement | null)?.isContentEditable;
      if (editing) return;
      const isUndo =
        (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z";
      const isRedoY =
        (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "y";
      const isRedoZ =
        (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "z";
      if (isUndo) {
        e.preventDefault();
        void doUndo();
      } else if (isRedoY || isRedoZ) {
        e.preventDefault();
        void doRedo();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function onStart(e: DragStartEvent) {
    const aId = String(e.active.id);
    const data = e.active.data.current as { name?: string } | undefined;
    setDraggingLabel(data?.name ?? null);
    if (aId.startsWith("bank:")) setDraggingType("bank");
    else if (aId.startsWith("folder:")) setDraggingType("folder");
    else setDraggingType(null);
  }

  async function onEnd(e: DragEndEvent) {
    const aId = String(e.active.id);
    const oId = e.over?.id ? String(e.over.id) : null;
    setDraggingLabel(null);
    setDraggingType(null);
    if (!oId) return;
    if (!oId.startsWith("tgt:")) return;
    const targetRaw = oId.slice("tgt:".length);
    const targetFolderId: string | null =
      targetRaw === "root" || targetRaw === "no-folder" ? null : targetRaw;

    if (aId.startsWith("bank:")) {
      const bankId = aId.slice("bank:".length);
      const bank = banksData.value.find((b) => b.id === bankId);
      if (!bank) return;
      const from = bank.folderId ?? null;
      if (from === targetFolderId) {
        // Já está nessa pasta — feedback visual em vez de silêncio.
        // Antes o aluno achava que o drag tinha falhado. [[feedback Karol 2026-05-25]]
        const folderName = targetFolderId
          ? foldersData.value.find((f) => f.id === targetFolderId)?.name ?? "essa pasta"
          : "raiz";
        void openAlert({
          title: "Já está aqui",
          message: `"${bank.name}" já está em ${folderName}. Nada pra mover.`
        });
        return;
      }
      try {
        await call("/api/student/bank-folders?action=move", {
          method: "POST",
          body: JSON.stringify({
            bankIds: [bankId],
            folderId: targetFolderId
          })
        });
        pushUndo({
          kind: "bank-move",
          bankId,
          bankName: bank.name,
          from,
          to: targetFolderId
        });
        await Promise.all([
          loadFolders({ silent: true }),
          loadBanks({ silent: true })
        ]);
      } catch (err) {
        void openAlert({
          title: "Não consegui mover",
          message: friendlyError(err)
        });
      }
      return;
    }

    if (aId.startsWith("folder:")) {
      const folderId = aId.slice("folder:".length);
      const folder = foldersData.value.find((f) => f.id === folderId);
      if (!folder) return;
      const from = folder.parentId;
      if (from === targetFolderId) return;
      if (folderId === targetFolderId) return;
      // Cycle: target não pode ser descendente da pasta movida
      const desc = descendantIds(folderId, foldersData.value);
      if (targetFolderId && desc.has(targetFolderId)) {
        void openAlert({
          title: "Movimento inválido",
          message: "Você não pode mover uma pasta para dentro de uma subpasta dela mesma."
        });
        return;
      }
      try {
        await call(
          `/api/student/bank-folders?id=${encodeURIComponent(folderId)}`,
          {
            method: "PATCH",
            body: JSON.stringify({ parentId: targetFolderId })
          }
        );
        pushUndo({
          kind: "folder-reparent",
          folderId,
          folderName: folder.name,
          from,
          to: targetFolderId
        });
        if (targetFolderId) expandFolder(targetFolderId);
        await Promise.all([
          loadFolders({ silent: true }),
          loadBanks({ silent: true })
        ]);
      } catch (err) {
        void openAlert({
          title: "Não consegui reorganizar",
          message: friendlyError(err)
        });
      }
      return;
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onStart}
      onDragEnd={onEnd}
    >
      <div class="flex h-full flex-col gap-3">
        <ExplorerTopbar />
        <StudyGroupsShortcut />
        <div class="flex min-h-[60vh] flex-col gap-3 lg:flex-row">
          <FolderTreeAside />
          <div class="min-w-0 flex-1">
            <ExplorerContent />
          </div>
        </div>
        <UndoHistoryBar />
      </div>
      <DragOverlay dropAnimation={null}>
        {draggingLabel ? (
          <div
            class={`pointer-events-none rounded-md border bg-card px-3 py-1.5 text-xs font-semibold text-foreground shadow-2xl ${
              draggingType === "folder"
                ? "border-amber-500"
                : "border-primary"
            }`}
          >
            {draggingType === "folder" ? (
              <FolderClosed className="mr-1 inline size-3.5" />
            ) : (
              <Package className="mr-1 inline size-3.5" />
            )}
            {draggingLabel}
          </div>
        ) : null}
      </DragOverlay>
      <ContextMenuOverlay />
    </DndContext>
  );
}

/** Atalho discreto pra Grupos de Estudo: aparece logo abaixo da topbar do
 *  Acervo quando o aluno tem pelo menos um grupo. Cada chip leva direto pro
 *  detalhe do grupo na view dedicada. Read-only — bancos de grupo ficam por
 *  lá, não misturados com pastas pessoais. [[project_study_groups]] */
function StudyGroupsShortcut() {
  // Carrega lazy quando o Acervo abre; sem efeito se já carregado.
  useEffect(() => {
    if (studyGroupsPhase.value === "idle") void loadStudyGroups({ silent: true });
  }, []);
  const groups = studyGroupsList.value;
  if (groups.length === 0) return null;
  return (
    <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2">
      <div class="mb-1.5 flex items-center justify-between gap-2">
        <span class="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300">
          <UsersIcon className="size-3.5" />
          Grupos de Estudo
        </span>
        <button
          type="button"
          title="Abrir Grupos de Estudo"
          aria-label="Abrir Grupos de Estudo"
          onClick={() => setActiveNav("gruposestudo")}
          class="inline-flex items-center gap-0.5 text-[11px] text-violet-600 transition hover:underline dark:text-violet-400"
        >
          Ver todos
          <ChevronRightIcon className="size-3" />
        </button>
      </div>
      <div class="flex flex-wrap gap-1.5">
        {groups.slice(0, 6).map((g) => (
          <button
            key={g.id}
            type="button"
            title={`Abrir grupo "${g.name}"`}
            aria-label={`Abrir grupo ${g.name}`}
            onClick={() => {
              setActiveNav("gruposestudo");
              openStudyGroup(g.id);
            }}
            class="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs transition hover:border-violet-500 hover:text-violet-700 dark:hover:text-violet-300"
          >
            <UsersIcon className="size-3 text-violet-500" />
            <span class="max-w-[140px] truncate">{g.name}</span>
            <span class="text-[10px] text-muted-foreground">
              {g.itemCount} {g.itemCount === 1 ? "item" : "itens"}
            </span>
          </button>
        ))}
        {groups.length > 6 && (
          <span class="text-[11px] text-muted-foreground self-center">
            +{groups.length - 6} mais
          </span>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Topbar (busca + view mode + ações)
// =====================================================================

function ExplorerTopbar() {
  const [showNew, setShowNew] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const q = bankSearchQuery.value;
  const canUndo = undoStack.value.length > 0;
  const canRedo = redoStack.value.length > 0;

  return (
    <>
      <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div class="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={q}
              onInput={(e) =>
                (bankSearchQuery.value = (e.target as HTMLInputElement).value)
              }
              placeholder="Buscar banco por nome ou descrição..."
              aria-label="Buscar banco"
              class="w-full rounded-md border border-border bg-background py-2 pl-9 pr-9 text-sm outline-none focus:border-primary"
            />
            {q && (
              <button
                type="button"
                onClick={() => (bankSearchQuery.value = "")}
                aria-label="Limpar busca"
                title="Limpar busca"
                class="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
          <div class="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowNew(true)}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-primary hover:bg-accent"
              title="Criar nova pasta na raiz"
            >
              <FolderPlus className="size-3.5" /> Nova pasta
            </button>
            <button
              type="button"
              onClick={() => setShowMerge(true)}
              class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-primary hover:bg-accent"
              title="Junta vários bancos num só (na ordem que você escolher)"
            >
              <Layers className="size-3.5" /> Mesclar bancos
            </button>
            {/* "Subir banco via IA" DESATIVADO 2026-05-30 (user): extração
                instável (sub-extração + schema). Esconder o ponto de entrada
                até a feature ficar confiável. Backend também tem kill-switch
                em ai-ingest-bank.ts. Pra reativar: descomentar + remover o
                guard do backend. [[project-pdf-to-dissertative-bank-pipeline]]
            <button
              type="button"
              onClick={openUploadBankModal}
              class="inline-flex items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-500/15 dark:text-violet-300"
              title="Cole texto bruto e a IA estrutura em questões"
            >
              <Sparkles class="size-3.5" />
              Subir banco via IA
            </button>
            */}
          </div>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void doUndo()}
              disabled={!canUndo || undoBusy.value}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-card/60 px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              title="Desfazer (Ctrl+Z)"
            >
              {undoBusy.value ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Undo2 className="size-3" />
              )}
              Desfazer
            </button>
            <button
              type="button"
              onClick={() => void doRedo()}
              disabled={!canRedo || undoBusy.value}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-card/60 px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              title="Refazer (Ctrl+Y)"
            >
              <Redo2 className="size-3" />
              Refazer
            </button>
          </div>
          <div class="flex items-center gap-1.5">
            <OrganizeToggle
              active={acervoEditMode.value}
              onToggle={() => (acervoEditMode.value = !acervoEditMode.value)}
            />
            <ViewModeToggle mode={bankViewMode.value} onChange={setBankViewMode} />
          </div>
        </div>
      </div>
      {acervoEditMode.value && (
        <div class="rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300">
          <Move className="mr-1 inline size-3" />
          Modo organizar ativo: arraste bancos e pastas pra reorganizar. Toque em
          <strong> Concluir</strong> pra voltar a deslizar a tela normalmente.
        </div>
      )}
      {showNew && (
        <NewFolderModal
          parentId={null}
          parentName={null}
          allowPickParent
          onClose={() => setShowNew(false)}
        />
      )}
      {showMerge && <MergeBanksModal onClose={() => setShowMerge(false)} />}
    </>
  );
}

function ViewModeToggle({
  mode,
  onChange
}: {
  mode: BankViewMode;
  onChange: (m: BankViewMode) => void;
}) {
  const opts: Array<{ key: BankViewMode; label: string; icon: typeof LayoutGrid }> =
    [
      { key: "cards", label: "Cards", icon: LayoutGrid },
      { key: "compact", label: "Compacto", icon: LayoutList },
      { key: "list", label: "Lista", icon: List }
    ];
  return (
    <div class="inline-flex shrink-0 items-center gap-0.5 rounded-md border border-border bg-card/60 p-0.5">
      {opts.map((o) => {
        const Icon = o.icon;
        const active = mode === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            title={o.label}
            aria-label={`Visualização ${o.label}`}
            class={`grid size-7 place-items-center rounded transition-colors ${
              active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent"
            }`}
          >
            <Icon className="size-3.5" />
          </button>
        );
      })}
    </div>
  );
}

// =====================================================================
// Tree (esquerda)
// =====================================================================

function FolderTreeAside() {
  const folders = foldersData.value;
  const banks = banksData.value;
  const tree = useMemo(() => buildTree(folders), [folders]);

  // Drag handler na borda direita pra redimensionar. Persistido em
  // localStorage. Min 200px, max 520px. [[feedback Karol 2026-05-25]]
  function startResize(e: PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = sidebarWidth.value;
    function onMove(ev: PointerEvent) {
      const next = Math.max(
        SIDEBAR_W_MIN,
        Math.min(SIDEBAR_W_MAX, startW + (ev.clientX - startX))
      );
      sidebarWidth.value = next;
    }
    function onUp() {
      writeSidebarWidth(sidebarWidth.value);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  return (
    <aside
      class="relative w-full shrink-0 rounded-md border border-border bg-card/60 p-2"
      style={{ width: `${sidebarWidth.value}px`, maxWidth: "100%" }}
    >
      <div class="mb-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Pastas
      </div>
      <div class="space-y-0.5">
        <TreeRowRoot banks={banks} />
        <TreeRowNoFolder banks={banks} />
        {tree.map((node) => (
          <TreeRow
            key={node.id}
            node={node}
            folders={folders}
            banks={banks}
          />
        ))}
        {tree.length === 0 && (
          <p class="px-2 py-3 text-[11px] text-muted-foreground">
            Você ainda não tem pastas. Use "Nova pasta" acima para começar a organizar.
          </p>
        )}
      </div>
      {/* Handle de resize na borda direita. 6px de largura, cobre 100% altura.
          Hover destaca cor; arrasto atualiza signal. Só renderiza em desktop
          (hidden em mobile via media-query do touch-action). */}
      <div
        onPointerDown={(e) => startResize(e as unknown as PointerEvent)}
        title="Arraste para redimensionar (200px–520px)"
        aria-label="Redimensionar sidebar de pastas"
        role="separator"
        class="absolute -right-0.5 top-0 hidden h-full w-1.5 cursor-col-resize touch-none select-none bg-transparent transition-colors hover:bg-primary/30 lg:block"
      />
    </aside>
  );
}

function TreeRowRoot({ banks }: { banks: BankRow[] }) {
  const active = explorerSelection.value === null;
  const { setNodeRef, isOver } = useDroppable({ id: "tgt:root" });
  return (
    <div ref={setNodeRef} class="relative">
      <button
        type="button"
        onClick={() => selectFolder(null)}
        class={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-semibold transition-colors ${
          active
            ? "bg-primary text-primary-foreground"
            : "text-foreground hover:bg-accent"
        } ${isOver ? "ring-2 ring-violet-500" : ""}`}
      >
        <Home className="size-3.5" />
        <span class="flex-1">Meu acervo</span>
        <span
          class={`rounded-full px-1.5 text-[9px] font-bold ${
            active
              ? "bg-primary-foreground/90 text-primary"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {banks.length}
        </span>
      </button>
    </div>
  );
}

function TreeRowNoFolder({ banks }: { banks: BankRow[] }) {
  const active = explorerSelection.value === "no-folder";
  const count = banks.filter((b) => !b.folderId).length;
  const { setNodeRef, isOver } = useDroppable({ id: "tgt:no-folder" });
  return (
    <div ref={setNodeRef}>
      <button
        type="button"
        onClick={() => selectFolder("no-folder")}
        class={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium transition-colors ${
          active
            ? "bg-primary text-primary-foreground"
            : "text-foreground hover:bg-accent"
        } ${isOver ? "ring-2 ring-violet-500" : ""}`}
      >
        <FolderOpen className="size-3.5 opacity-70" />
        <span class="flex-1">Sem pasta (raiz)</span>
        <span
          class={`rounded-full px-1.5 text-[9px] font-bold ${
            active
              ? "bg-primary-foreground/90 text-primary"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {count}
        </span>
      </button>
    </div>
  );
}

function TreeRow({
  node,
  folders,
  banks
}: {
  node: TreeNode;
  folders: FolderRow[];
  banks: BankRow[];
}) {
  const expanded = explorerExpanded.value.has(node.id);
  const active = explorerSelection.value === node.id;
  const total = totalBankCount(node.id, banks, folders);
  const hasChildren = node.children.length > 0;

  const { setNodeRef: dropRef, isOver } = useDroppable({
    id: `tgt:${node.id}`
  });
  const {
    attributes,
    listeners,
    setNodeRef: dragRef,
    isDragging
  } = useDraggable({
    id: `folder:${node.id}`,
    data: { name: node.name }
  });

  const [editing, setEditing] = useState(false);
  const [creatingSub, setCreatingSub] = useState(false);

  const treeMenuItems = () => folderContextMenuItems({
    name: node.name,
    onSelect: () => selectFolder(node.id),
    onCreateSub: () => setCreatingSub(true),
    onRename: () => setEditing(true),
    onDelete: () => void remove()
  });
  const longPress = useLongPress((x, y) => openContextMenuAt(x, y, treeMenuItems()));
  const dragOn = acervoEditMode.value;

  async function remove() {
    const ok = await openConfirm({
      title: "Apagar pasta?",
      message: `Bancos dentro de "${node.name}" vão para a raiz. Subpastas direto abaixo viram filhas do nível pai. A pasta é apagada para sempre. Continuar?`,
      confirmLabel: "Apagar pasta",
      danger: true
    });
    if (!ok) return;
    try {
      await call(
        `/api/student/bank-folders?id=${encodeURIComponent(node.id)}`,
        { method: "DELETE" }
      );
      if (explorerSelection.value === node.id) selectFolder(null);
      await Promise.all([
        loadFolders({ silent: true }),
        loadBanks({ silent: true })
      ]);
    } catch (err) {
      void openAlert({
        title: "Não consegui apagar",
        message: friendlyError(err)
      });
    }
  }

  return (
    <div>
      <div
        ref={dropRef}
        class={`group/row relative flex items-center gap-1 rounded-md transition-colors ${
          isDragging ? "opacity-40" : ""
        } ${isOver ? "ring-2 ring-violet-500" : ""}`}
        style={{ paddingLeft: `${node.depth * 12}px` }}
        onPointerDown={(e) => longPress.onPointerDown(e as unknown as PointerEvent)}
        onPointerMove={(e) => longPress.onPointerMove(e as unknown as PointerEvent)}
        onPointerUp={() => longPress.onPointerUp()}
        onPointerCancel={() => longPress.onPointerUp()}
        onContextMenu={(e) => openContextMenu(e, treeMenuItems())}
      >
        <button
          type="button"
          onClick={() => toggleExpanded(node.id)}
          class={`grid size-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-accent ${
            !hasChildren ? "invisible" : ""
          }`}
          title={expanded ? "Recolher" : "Expandir"}
          aria-label={expanded ? "Recolher pasta" : "Expandir pasta"}
        >
          {expanded ? (
            <ChevronDown className="size-3" />
          ) : (
            <ChevronRight className="size-3" />
          )}
        </button>
        <button
          type="button"
          onClick={() => selectFolder(node.id)}
          class={`flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left text-xs font-medium transition-colors ${
            active
              ? "bg-primary text-primary-foreground"
              : "text-foreground hover:bg-accent"
          }`}
          title={node.name}
        >
          {expanded ? (
            <FolderOpen className="size-3.5 shrink-0 text-amber-500" />
          ) : (
            <FolderClosed className="size-3.5 shrink-0 text-amber-500" />
          )}
          <span class="min-w-0 flex-1 truncate">{node.name}</span>
          {total > 0 && (
            <span
              class={`rounded-full px-1.5 text-[9px] font-bold ${
                active
                  ? "bg-primary-foreground/90 text-primary"
                  : "bg-muted text-muted-foreground"
              }`}
              title={`${total} bancos (incluindo subpastas)`}
            >
              {total}
            </span>
          )}
        </button>
        {dragOn && (
          <button
            ref={dragRef}
            type="button"
            {...attributes}
            {...listeners}
            title={`Arrastar "${node.name}"`}
            aria-label={`Arrastar pasta ${node.name}`}
            class="grid size-5 shrink-0 cursor-grab touch-none place-items-center rounded text-muted-foreground opacity-100 transition-colors hover:bg-accent active:cursor-grabbing"
          >
            <GripVertical className="size-3" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openContextMenu(e as unknown as MouseEvent, treeMenuItems());
          }}
          onPointerDown={(e) => e.stopPropagation()}
          class="grid size-6 shrink-0 place-items-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-accent group-hover/row:opacity-100 focus:opacity-100"
          aria-label={`Ações da pasta ${node.name}`}
        >
          <MoreVertical className="size-3" />
        </button>
      </div>
      {expanded &&
        node.children.map((child) => (
          <TreeRow
            key={child.id}
            node={child}
            folders={folders}
            banks={banks}
          />
        ))}
      {editing && (
        <RenameFolderModal
          folder={node}
          onClose={() => setEditing(false)}
        />
      )}
      {creatingSub && (
        <NewFolderModal
          parentId={node.id}
          parentName={node.name}
          onClose={() => setCreatingSub(false)}
        />
      )}
    </div>
  );
}

// =====================================================================
// Content (direita)
// =====================================================================

function ExplorerContent() {
  const sel = explorerSelection.value;
  const folders = foldersData.value;
  const banks = banksData.value;
  const q = bankSearchQuery.value.trim().toLowerCase();
  const mode = bankViewMode.value;

  const filteredBanks = useMemo(() => {
    let arr = banks;
    if (sel === "no-folder") {
      arr = arr.filter((b) => !b.folderId);
    } else if (sel === null) {
      // Raiz: bancos sem pasta + nada
      arr = arr.filter((b) => !b.folderId);
    } else if (typeof sel === "string") {
      const desc = descendantIds(sel, folders);
      desc.add(sel);
      arr = arr.filter((b) => b.folderId && desc.has(b.folderId));
    }
    if (q) {
      arr = arr.filter((b) => {
        const hay =
          b.name.toLowerCase() +
          " " +
          (b.description ? b.description.toLowerCase() : "") +
          " " +
          (b.sharedByName ? b.sharedByName.toLowerCase() : "");
        return hay.includes(q);
      });
    }
    return arr;
  }, [banks, folders, sel, q]);

  const childFolders =
    sel === null
      ? childFoldersOf(null, folders)
      : typeof sel === "string" && sel !== "no-folder"
      ? childFoldersOf(sel, folders)
      : [];

  const folderTitle = (() => {
    if (sel === null) return "Meu acervo";
    if (sel === "no-folder") return "Sem pasta (raiz)";
    const f = folders.find((x) => x.id === sel);
    return f ? f.name : "Pasta";
  })();

  const breadcrumb = useMemo(() => {
    if (typeof sel !== "string" || sel === "no-folder") return [] as FolderRow[];
    const chain: FolderRow[] = [];
    let cur: string | null = sel;
    while (cur) {
      const f = folders.find((x) => x.id === cur);
      if (!f) break;
      chain.unshift(f);
      cur = f.parentId;
    }
    return chain;
  }, [sel, folders]);

  return (
    <section class="min-h-full rounded-md border border-border bg-card/60 p-3">
      <ContentHeader
        title={folderTitle}
        breadcrumb={breadcrumb}
        sel={sel}
        bankCount={filteredBanks.length}
        folderCount={childFolders.length}
      />

      {/* Tiles de pastas (raiz ou pasta com subpastas) */}
      {childFolders.length > 0 && (
        <div class="mb-3">
          <h3 class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Pastas ({childFolders.length})
          </h3>
          <div class="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
            {childFolders.map((f) => (
              <FolderTile
                key={f.id}
                folder={f}
                folders={folders}
                banks={banks}
              />
            ))}
          </div>
        </div>
      )}

      {/* Banks */}
      {filteredBanks.length === 0 ? (
        <EmptyState sel={sel} q={q} />
      ) : (
        <div>
          <h3 class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {sel === null
              ? `Bancos sem pasta (${filteredBanks.length})`
              : `Bancos (${filteredBanks.length})`}
          </h3>
          {mode === "list" ? (
            <div class="overflow-hidden rounded-md border border-border bg-background/40">
              {filteredBanks.map((b, i) => (
                <DraggableBankWrap key={b.id} bank={b}>
                  <BankListRow
                    bank={b}
                    isLast={i === filteredBanks.length - 1}
                  />
                </DraggableBankWrap>
              ))}
            </div>
          ) : (
            <div
              class={
                mode === "compact"
                  ? "grid auto-rows-fr grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
                  : "grid auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
              }
            >
              {filteredBanks.map((b) => (
                <DraggableBankWrap key={b.id} bank={b}>
                  <BankCard bank={b} compact={mode === "compact"} />
                </DraggableBankWrap>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ContentHeader({
  title,
  breadcrumb,
  sel,
  bankCount,
  folderCount
}: {
  title: string;
  breadcrumb: FolderRow[];
  sel: Selection;
  bankCount: number;
  folderCount: number;
}) {
  return (
    <header class="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2.5">
      <div class="min-w-0">
        {/* Breadcrumb */}
        <div class="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
          <button
            type="button"
            onClick={() => selectFolder(null)}
            class="inline-flex items-center gap-1 rounded hover:text-foreground"
          >
            <Home className="size-3" /> Meu acervo
          </button>
          {sel === "no-folder" && (
            <>
              <ChevronRight className="size-3" />
              <span class="text-foreground">Sem pasta</span>
            </>
          )}
          {breadcrumb.map((f, i) => (
            <span key={f.id} class="inline-flex items-center gap-1">
              <ChevronRight className="size-3" />
              {i === breadcrumb.length - 1 ? (
                <span class="font-semibold text-foreground">{f.name}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => selectFolder(f.id)}
                  class="hover:text-foreground"
                >
                  {f.name}
                </button>
              )}
            </span>
          ))}
        </div>
        <h2 class="mt-1 truncate text-lg font-bold">{title}</h2>
        <p class="text-[11px] text-muted-foreground">
          {folderCount > 0 && (
            <>
              {folderCount}{" "}
              {folderCount === 1 ? "subpasta" : "subpastas"}
            </>
          )}
          {folderCount > 0 && bankCount > 0 && (
            <span class="mx-1.5 opacity-50">·</span>
          )}
          {bankCount > 0 && (
            <>
              {bankCount} {bankCount === 1 ? "banco" : "bancos"}
            </>
          )}
          {folderCount === 0 && bankCount === 0 && "Pasta vazia"}
        </p>
      </div>
    </header>
  );
}

function FolderTile({
  folder,
  folders,
  banks
}: {
  folder: FolderRow;
  folders: FolderRow[];
  banks: BankRow[];
}) {
  const total = totalBankCount(folder.id, banks, folders);
  const subCount = childFoldersOf(folder.id, folders).length;
  const { setNodeRef: dropRef, isOver } = useDroppable({
    id: `tgt:${folder.id}`
  });
  const {
    attributes,
    listeners,
    setNodeRef: dragRef,
    isDragging
  } = useDraggable({
    id: `folder:${folder.id}`,
    data: { name: folder.name }
  });
  const [editing, setEditing] = useState(false);
  const [creatingSub, setCreatingSub] = useState(false);

  async function remove() {
    const ok = await openConfirm({
      title: "Apagar pasta?",
      message: `Bancos dentro de "${folder.name}" vão para a raiz. Subpastas direto abaixo viram filhas do nível pai. A pasta é apagada para sempre. Continuar?`,
      confirmLabel: "Apagar pasta",
      danger: true
    });
    if (!ok) return;
    try {
      await call(
        `/api/student/bank-folders?id=${encodeURIComponent(folder.id)}`,
        { method: "DELETE" }
      );
      if (explorerSelection.value === folder.id) selectFolder(null);
      await Promise.all([
        loadFolders({ silent: true }),
        loadBanks({ silent: true })
      ]);
    } catch (err) {
      void openAlert({
        title: "Não consegui apagar",
        message: friendlyError(err)
      });
    }
  }

  const menuItems = () => folderContextMenuItems({
    name: folder.name,
    onSelect: () => {
      selectFolder(folder.id);
      expandFolder(folder.id);
    },
    onCreateSub: () => setCreatingSub(true),
    onRename: () => setEditing(true),
    onDelete: () => void remove()
  });
  const longPress = useLongPress((x, y) => openContextMenuAt(x, y, menuItems()));
  // Grip de arrasto só aparece (e só recebe listeners) no modo organizar.
  const dragOn = acervoEditMode.value;

  // Wrap em DIV pra dnd-kit. Click no botão interno seleciona/expande; drag
  // pelo grip no canto move a pasta; right-click ou pressionar 1.5s abrem
  // o menu de contexto. [[project_drag_drop_banks_folders]]
  return (
    <div
      ref={dropRef}
      class={`group/dnd relative flex flex-col items-start gap-1 rounded-md border bg-card p-3 text-left transition-all hover:border-primary hover:bg-accent ${
        isDragging ? "opacity-40" : ""
      } ${
        isOver
          ? "border-violet-500 ring-2 ring-violet-500"
          : "border-border"
      }`}
      onPointerDown={(e) => longPress.onPointerDown(e as unknown as PointerEvent)}
      onPointerMove={(e) => longPress.onPointerMove(e as unknown as PointerEvent)}
      onPointerUp={() => longPress.onPointerUp()}
      onPointerCancel={() => longPress.onPointerUp()}
      onContextMenu={(e) => openContextMenu(e, menuItems())}
    >
      <button
        type="button"
        onClick={() => {
          selectFolder(folder.id);
          expandFolder(folder.id);
        }}
        class="flex w-full flex-col items-start gap-1 text-left"
        title={folder.name}
      >
        <FolderClosed className="size-7 text-amber-500" />
        <span class="line-clamp-2 w-full text-xs font-bold">{folder.name}</span>
        <span class="text-[10px] text-muted-foreground">
          {total} {total === 1 ? "banco" : "bancos"}
          {subCount > 0 && ` · ${subCount} ${subCount === 1 ? "sub" : "subs"}`}
        </span>
      </button>
      {dragOn && (
        <button
          ref={dragRef}
          type="button"
          {...attributes}
          {...listeners}
          title={`Arrastar pasta "${folder.name}"`}
          aria-label={`Arrastar pasta ${folder.name}`}
          class="absolute right-1 top-1 z-20 grid size-6 cursor-grab touch-none place-items-center rounded-md border border-primary/30 bg-card/90 text-muted-foreground opacity-100 shadow-sm transition-colors hover:bg-primary hover:text-primary-foreground active:cursor-grabbing"
        >
          <GripVertical className="size-3" />
        </button>
      )}
      {editing && (
        <RenameFolderModal folder={folder} onClose={() => setEditing(false)} />
      )}
      {creatingSub && (
        <NewFolderModal
          parentId={folder.id}
          parentName={folder.name}
          onClose={() => setCreatingSub(false)}
        />
      )}
    </div>
  );
}

function DraggableBankWrap({
  bank,
  children
}: {
  bank: BankRow;
  children: ComponentChildren;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `bank:${bank.id}`,
    data: { name: bank.name }
  });

  async function moveToFolder(targetFolderId: string | null) {
    const from = bank.folderId ?? null;
    if (from === targetFolderId) return;
    try {
      await call("/api/student/bank-folders?action=move", {
        method: "POST",
        body: JSON.stringify({ bankIds: [bank.id], folderId: targetFolderId })
      });
      pushUndo({
        kind: "bank-move",
        bankId: bank.id,
        bankName: bank.name,
        from,
        to: targetFolderId
      });
      await Promise.all([
        loadFolders({ silent: true }),
        loadBanks({ silent: true })
      ]);
    } catch (err) {
      void openAlert({
        title: "Não consegui mover",
        message: friendlyError(err)
      });
    }
  }

  async function removeFromAcervo() {
    const fromLibrary = !!bank.importedFromLibraryBankId;
    const ownedPure =
      bank.sources?.some((s) => s.kind === "owned") && !fromLibrary;
    // 3 mensagens diferentes pelo cenário pra deixar claro o que vai acontecer:
    //   - Cópia da biblioteca: re-importável, perde progresso
    //   - Grant compartilhado: dono pode te enviar de novo, perde progresso
    //   - Owned puro (upload, gerado de caderno): DELETA em definitivo
    const message = ownedPure
      ? `"${bank.name}" será REMOVIDO EM DEFINITIVO. Esta ação não pode ser desfeita — o banco, todas as questões e seu progresso serão apagados. Tem certeza?`
      : fromLibrary
        ? `"${bank.name}" sai da sua lista. Você pode reimportar pela Biblioteca de Provas a qualquer momento. Seu progresso neste banco será perdido.`
        : `"${bank.name}" sai da sua lista. O dono pode te enviar de novo no futuro. Seu progresso neste banco será perdido.`;
    const ok = await openConfirm({
      title: ownedPure ? "Apagar banco permanentemente?" : "Remover do acervo?",
      message,
      confirmLabel: ownedPure ? "Apagar para sempre" : "Remover",
      danger: true
    });
    if (!ok) return;
    try {
      const resp = await call<{ message?: string }>(
        `/api/student/banks?id=${encodeURIComponent(bank.id)}`,
        { method: "DELETE" }
      );
      await Promise.all([
        loadFolders({ silent: true }),
        loadBanks({ silent: true })
      ]);
      void openAlert({
        title: "Removido do acervo",
        message: resp.message ?? `"${bank.name}" foi removido com sucesso.`
      });
    } catch (err) {
      void openAlert({
        title: "Não consegui remover",
        message: friendlyError(err)
      });
    }
  }

  // Itens do menu de contexto (compartilhado entre right-click, long-press e
  // botão 3-pontinhos). "Mover para…" abre submenu in-place listando pastas.
  const menuItems = () => bankContextMenuItems({
    bank,
    onOpen: () => {
      setActiveNav("bancos");
      openBank(bank.id);
    },
    onMoveTo: (folderId) => void moveToFolder(folderId),
    onRemoveFromAcervo: () => void removeFromAcervo()
  });

  // Long-press (segurar 1.5s sem mover) — abre o mesmo menu pra quem usa
  // touch ou notebook sem botão direito. [[feedback Karol 2026-05-24]]
  const longPress = useLongPress((x, y) => openContextMenuAt(x, y, menuItems()));

  // dnd-kit registra `onPointerDown` via `{...listeners}` — pra co-existir
  // com long-press, mergimos os dois handlers explicitamente em vez de
  // spread no fim. Spread último venceria e desativaria o long-press.
  const dndPointerDown = (listeners as { onPointerDown?: (e: PointerEvent) => void })
    .onPointerDown;
  const restListeners = { ...listeners } as Record<string, unknown>;
  delete restListeners.onPointerDown;

  // Arrasto só quando o "modo organizar" está ligado. Desligado (default), o
  // card NÃO recebe os listeners do dnd-kit nem `touch-action:none`, então o
  // scroll por toque funciona normal no mobile. Click pra abrir, long-press e
  // menu de 3-pontinhos seguem funcionando nos dois modos.
  // [[feedback Luís 2026-05-27]]
  const editing = acervoEditMode.value;
  const dragHandlers = editing
    ? { ...restListeners, ...attributes }
    : {};

  // Card inteiro draggable agora que BankCard virou `<div role="button">` em
  // vez de `<button>`. Antes o <button> nativo capturava pointerdown e
  // impedia dnd-kit de iniciar o drag.
  // [[feedback Karol 2026-05-24: "clicando em qualquer lugar do bloco estava melhor"]]
  return (
    <div
      ref={setNodeRef}
      {...dragHandlers}
      onPointerDown={(e) => {
        if (editing) dndPointerDown?.(e as unknown as PointerEvent);
        longPress.onPointerDown(e as unknown as PointerEvent);
      }}
      onPointerMove={(e) => longPress.onPointerMove(e as unknown as PointerEvent)}
      onPointerUp={() => longPress.onPointerUp()}
      onPointerCancel={() => longPress.onPointerUp()}
      style={editing ? { touchAction: "none" } : undefined}
      class={`group/dnd relative flex h-full ${isDragging ? "opacity-40" : ""} ${
        editing
          ? "cursor-grab rounded-lg outline-dashed outline-1 outline-violet-400/60"
          : ""
      }`}
      title={
        editing
          ? "Arraste pra qualquer pasta · botão direito ou pressionar 1.5s pra mais ações"
          : undefined
      }
      onContextMenu={(e) => openContextMenu(e, menuItems())}
    >
      {children}
      {/* 3-pontinhos sempre visível (foco-claro em hover) — aparece em todos
          modos (cards/compacto/lista). Click abre o mesmo menu de right-click
          + long-press. [[feedback Karol 2026-05-24]] */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          openContextMenu(e as unknown as MouseEvent, menuItems());
        }}
        onPointerDown={(e) => e.stopPropagation()}
        title="Mais ações"
        aria-label={`Mais ações de ${bank.name}`}
        class="absolute right-1 top-1 z-20 grid size-7 place-items-center rounded-md border border-border/40 bg-card/90 text-muted-foreground opacity-0 shadow-sm backdrop-blur transition-opacity hover:bg-accent hover:text-foreground group-hover/dnd:opacity-100 focus:opacity-100"
      >
        <MoreVertical className="size-3.5" />
      </button>
    </div>
  );
}

function BankListRow({ bank, isLast }: { bank: BankRow; isLast: boolean }) {
  const pct = bank.estudo.accuracyPct;
  return (
    <button
      type="button"
      onClick={() => {
        setActiveNav("bancos");
        openBank(bank.id);
      }}
      class={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-accent/40 ${
        isLast ? "" : "border-b border-border/60"
      }`}
    >
      <div class="grid size-8 shrink-0 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
        <Package className="size-3.5" />
      </div>
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-semibold">{bank.name}</div>
        <div class="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
          <span>{bank.questionCount} questões</span>
          <span class="opacity-50">·</span>
          <span>
            {bank.estudo.respondidas} respondidas
            {pct != null && ` · ${pct}% acerto`}
          </span>
          {bank.provas.emAndamento && (
            <>
              <span class="opacity-50">·</span>
              <span class="font-semibold text-amber-600 dark:text-amber-400">
                Prova em andamento
              </span>
            </>
          )}
          {(bank.dissertativeCount ?? 0) > 0 && (
            <>
              <span class="opacity-50">·</span>
              <span
                class="inline-flex items-center gap-0.5 rounded-md bg-fuchsia-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-fuchsia-700 ring-1 ring-fuchsia-500/30 dark:text-fuchsia-300"
                title={`Versão dissertativa gerada por IA: ${bank.dissertativeCount} questão${bank.dissertativeCount !== 1 ? "ões" : ""}`}
              >
                <Sparkles width="0.7em" height="0.7em" />
                IA Disc.
              </span>
            </>
          )}
        </div>
      </div>
    </button>
  );
}

function EmptyState({ sel, q }: { sel: Selection; q: string }) {
  // Guidance contextual — separa busca sem match, raiz vazia e pasta vazia.
  // Padrão visual do empty state do admin: ícone + título + body + CTA.
  let title: string;
  let body: string;
  let Icon = Package as typeof Package;
  if (q) {
    title = `Nenhum banco com "${q}"`;
    body = "Tente uma palavra-chave diferente ou limpe a busca.";
    Icon = Search;
  } else if (sel === null) {
    title = "Nenhum banco fora de pastas";
    body =
      "Os bancos que você criar ou importar e não categorizar em pastas aparecem aqui.";
  } else if (sel === "no-folder") {
    title = "Você organizou tudo em pastas";
    body =
      "Para mover um banco para a raiz, arraste-o para 'Sem pasta' na barra lateral.";
  } else {
    title = "Pasta vazia";
    body =
      "Arraste bancos do acervo para cá, crie subpastas, ou importe um banco da Biblioteca de provas.";
  }
  return (
    <div class="rounded-md border border-dashed border-border bg-background/40 px-4 py-10 text-center">
      <Icon className="mx-auto mb-3 size-8 text-muted-foreground" />
      <p class="text-sm font-semibold">{title}</p>
      <p class="mt-1 text-xs text-muted-foreground">{body}</p>
    </div>
  );
}

// =====================================================================
// Undo bar
// =====================================================================

function UndoHistoryBar() {
  const folders = foldersData.value;
  const stack = undoStack.value;
  if (stack.length === 0) return null;
  const last = stack.slice(-3).reverse();
  return (
    <div class="flex items-start gap-2 rounded-md border border-violet-500/20 bg-violet-500/5 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300">
      <History className="mt-0.5 size-3.5 shrink-0" />
      <div class="min-w-0 flex-1">
        <p class="mb-1 font-bold">
          Histórico ({stack.length}) — Ctrl+Z para desfazer, Ctrl+Y para refazer
        </p>
        <ul class="space-y-0.5">
          {last.map((op, i) => (
            <li
              key={i}
              class="truncate font-mono text-[10px] text-violet-600 dark:text-violet-400"
            >
              {i + 1 === 1 ? "→ " : "   "}
              {describeOp(op, folders)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// =====================================================================
// Modais: nova pasta / renomear
// =====================================================================

// Opções de pasta-pai pra um <select>, achatadas e indentadas por nível.
function buildFolderOptions(
  folders: FolderRow[]
): Array<{ id: string; label: string }> {
  const byParent = new Map<string | null, FolderRow[]>();
  for (const f of folders) {
    const k = f.parentId ?? null;
    if (!byParent.has(k)) byParent.set(k, []);
    byParent.get(k)!.push(f);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  }
  const out: Array<{ id: string; label: string }> = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const f of byParent.get(parentId) ?? []) {
      const indent = "   ".repeat(depth);
      out.push({ id: f.id, label: `${indent}${depth > 0 ? "└ " : ""}${f.name}` });
      walk(f.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

function NewFolderModal({
  parentId,
  parentName,
  onClose,
  allowPickParent = false
}: {
  parentId: string | null;
  parentName: string | null;
  onClose: () => void;
  /** Quando true, mostra um seletor de pasta-pai (escolher ONDE criar). */
  allowPickParent?: boolean;
}) {
  const [name, setName] = useState("");
  const [parent, setParent] = useState<string | null>(parentId);
  const folderOptions = useMemo(
    () => buildFolderOptions(foldersData.value),
    [foldersData.value]
  );
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Foco automático no input ao abrir. O `autoFocus` nativo falha em alguns
  // casos (modal montado via portal, animação de entrada, foco roubado pelo
  // close-btn ao renderizar) — ref + rAF garante. [[feedback Karol 2026-05-24]]
  useEffect(() => {
    const handle = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(handle);
  }, []);

  // ESC fecha o modal; teclas que viajam pra cima são preservadas SÓ pro
  // próprio modal (Enter submete, navegação dentro do input funciona).
  // Captura no overlay externo pra qualquer foco interno disparar — e
  // impede que atalhos globais do explorer (Ctrl+Z undo, etc.) executem em
  // background enquanto a modal está aberta.
  function onOverlayKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    // Bloqueia bubbling de Ctrl/Cmd + qualquer letra (undo/redo/etc) — sem
    // isso, o aluno digitando "z" com Ctrl pressionado dispararia o undo
    // do explorer atrás.
    if ((e.ctrlKey || e.metaKey) && e.key.length === 1) {
      e.stopPropagation();
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) {
      setErr("Dê um nome para a pasta.");
      return;
    }
    setSubmitting(true);
    try {
      const targetParent = allowPickParent ? parent : parentId;
      const body: Record<string, unknown> = { name: name.trim() };
      if (targetParent) body.parentId = targetParent;
      await call("/api/student/bank-folders", {
        method: "POST",
        body: JSON.stringify(body)
      });
      await loadFolders({ silent: true });
      if (targetParent) expandFolder(targetParent);
      onClose();
    } catch (err) {
      setErr(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4"
      onKeyDown={onOverlayKeyDown}
    >
      <div class="w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <FolderPlus className="size-5 text-primary" />
            {parentName ? `Nova subpasta em "${parentName}"` : "Nova pasta"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <form onSubmit={submit} class="space-y-3">
          {allowPickParent && (
            <label class="block">
              <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Onde criar
              </span>
              <select
                value={parent ?? ""}
                onChange={(e) =>
                  setParent((e.target as HTMLSelectElement).value || null)
                }
                aria-label="Pasta onde criar"
                class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              >
                <option value="">📁 Raiz do acervo (sem pasta)</option>
                {folderOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label class="block">
            <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Nome da pasta
            </span>
            <input
              ref={inputRef}
              type="text"
              required
              maxLength={80}
              value={name}
              onInput={(e) =>
                setName((e.target as HTMLInputElement).value)
              }
              placeholder="ex: Cálculo, ENEM 2024..."
              aria-label="Nome da pasta"
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </label>
          {err && <p class="text-xs text-destructive">{err}</p>}
          <div class="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {submitting && <Loader2 className="size-3.5 animate-spin" />}
              Criar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =====================================================================
// Mesclar bancos — junta vários bancos num só, na ordem escolhida.
// Originais ficam intactos; duplicadas (mesmo enunciado) são removidas.
// [[project-pdf-to-dissertative-bank-pipeline]]
// =====================================================================

function MergeBanksModal({ onClose }: { onClose: () => void }) {
  // Ordem de seleção = ordem da mesclagem. Aluno reordena com ↑/↓.
  const [selected, setSelected] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const allBanks = banksData.value;
  const byId = useMemo(
    () => new Map(allBanks.map((b) => [b.id, b])),
    [allBanks]
  );
  const filtered = useMemo(() => {
    const t = query.trim().toLowerCase();
    const list = [...allBanks].sort((a, b) => a.name.localeCompare(b.name));
    if (!t) return list;
    return list.filter(
      (b) =>
        b.name.toLowerCase().includes(t) ||
        (b.description ?? "").toLowerCase().includes(t)
    );
  }, [allBanks, query]);

  const selectedBanks = selected.map((id) => byId.get(id)).filter(Boolean) as BankRow[];
  const totalQuestoes = selectedBanks.reduce(
    (s, b) => s + (b.questionCount ?? 0),
    0
  );
  // Tamanho somado dos bancos selecionados. O banco mesclado vira UM payload
  // único; se passar do teto, abrir/estudar depois estouraria o cap de 64MB
  // do Neon. Avisamos AQUI, antes de enviar. Constante espelha o backend
  // (apps/api/src/lib/merge-limits.ts) — manter em sincronia.
  const MERGE_MAX_MB = 45;
  const MERGE_MAX_BYTES = MERGE_MAX_MB * 1024 * 1024;
  const totalBytes = selectedBanks.reduce((s, b) => s + (b.sizeBytes ?? 0), 0);
  const totalMb = totalBytes / (1024 * 1024);
  const overLimit = totalBytes > MERGE_MAX_BYTES;
  // Zona amarela: ≥80% do teto — ainda dá, mas avisa que está perto.
  const nearLimit = !overLimit && totalBytes >= MERGE_MAX_BYTES * 0.8;
  // Nome é opcional: sugerimos um default a partir dos bancos escolhidos.
  // Espelha a derivação do backend (student-banks-merge.ts) — se o aluno
  // deixar o campo vazio, mandamos essa sugestão.
  const suggestedName =
    selectedBanks.length === 2
      ? `${selectedBanks[0].name} + ${selectedBanks[1].name}`
      : selectedBanks.length > 2
        ? `${selectedBanks[0].name} + outros ${selectedBanks.length - 1} bancos`
        : "";

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }
  function move(idx: number, dir: -1 | 1) {
    setSelected((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }

  function onOverlayKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.length === 1) e.stopPropagation();
  }

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    if (selected.length < 2) {
      setErr("Escolha pelo menos 2 bancos pra mesclar.");
      return;
    }
    if (overLimit) {
      setErr(
        `Os bancos somam ${totalMb.toFixed(1)} MB, acima do limite de ${MERGE_MAX_MB} MB. ` +
          `Tire algum banco da seleção pra continuar.`
      );
      return;
    }
    setSubmitting(true);
    try {
      // Nome opcional: cai pra sugestão se vazio. O backend também tem o
      // próprio default, então mandar vazio também funcionaria.
      const res = await mergeBanks({
        bankIds: selected,
        name: name.trim() || suggestedName,
        dedupe: true
      });
      await loadBanks({ silent: true });
      onClose();
      openBank(res.bank.id);
    } catch (e2) {
      setErr(friendlyError(e2));
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = selected.length >= 2 && !submitting && !overLimit;

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4"
      onKeyDown={onOverlayKeyDown}
    >
      <div class="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-card shadow-xl">
        <div class="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Layers className="size-5 text-primary" />
            Mesclar bancos
          </h2>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>

        <form onSubmit={submit} class="flex min-h-0 flex-1 flex-col">
          <div class="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <label class="block">
              <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Nome do banco mesclado{" "}
                <span class="font-medium normal-case tracking-normal text-muted-foreground/70">
                  (opcional)
                </span>
              </span>
              <input
                type="text"
                maxLength={120}
                value={name}
                onInput={(e) => setName((e.target as HTMLInputElement).value)}
                placeholder={
                  suggestedName || "ex: Pediatria — Casos clínicos (completo)"
                }
                class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
              {!name.trim() && suggestedName && (
                <span class="mt-1 block text-[11px] text-muted-foreground">
                  Se deixar vazio, vamos usar:{" "}
                  <span class="font-medium text-foreground">{suggestedName}</span>
                </span>
              )}
            </label>

            {/* Picker de bancos */}
            <div>
              <div class="mb-1.5 flex items-center justify-between">
                <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Escolha os bancos ({selected.length} selecionados)
                </span>
                <div class="relative">
                  <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="search"
                    value={query}
                    onInput={(e) =>
                      setQuery((e.target as HTMLInputElement).value)
                    }
                    placeholder="Buscar..."
                    class="w-40 rounded-md border border-border bg-background py-1 pl-7 pr-2 text-xs outline-none focus:border-primary"
                  />
                </div>
              </div>
              {allBanks.length === 0 ? (
                <p class="rounded-md border border-border bg-background px-3 py-4 text-center text-xs text-muted-foreground">
                  Você ainda não tem bancos no acervo.
                </p>
              ) : (
                <div class="max-h-52 space-y-1 overflow-y-auto rounded-md border border-border bg-background p-1.5">
                  {filtered.map((b) => {
                    const checked = selected.includes(b.id);
                    return (
                      <label
                        key={b.id}
                        class="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggle(b.id)}
                        />
                        <span class="min-w-0 flex-1 truncate">{b.name}</span>
                        <span class="shrink-0 text-[11px] text-muted-foreground">
                          {b.questionCount} q
                        </span>
                      </label>
                    );
                  })}
                  {filtered.length === 0 && (
                    <p class="px-2 py-3 text-center text-xs text-muted-foreground">
                      Nenhum banco encontrado para "{query}".
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Ordem da mesclagem */}
            {selectedBanks.length > 0 && (
              <div>
                <div class="mb-1.5 flex items-center justify-between gap-2">
                  <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Ordem da mesclagem ({totalQuestoes} questões no total)
                  </span>
                  {totalBytes > 0 && (
                    <span
                      class={
                        overLimit
                          ? "text-[11px] font-semibold text-destructive"
                          : nearLimit
                            ? "text-[11px] font-semibold text-amber-600 dark:text-amber-400"
                            : "text-[11px] text-muted-foreground"
                      }
                      title={`Limite de ${MERGE_MAX_MB} MB por mesclagem`}
                    >
                      {totalMb < 0.1 ? "<0,1" : totalMb.toFixed(1)} / {MERGE_MAX_MB} MB
                    </span>
                  )}
                </div>
                {overLimit && (
                  <div class="mb-2 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[11px] text-destructive">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    <span>
                      Os bancos selecionados somam <strong>{totalMb.toFixed(1)} MB</strong>,
                      acima do limite de {MERGE_MAX_MB} MB. Bancos com muitas imagens
                      ocupam bastante espaço — tire algum banco da seleção (de
                      preferência os mais pesados) pra conseguir mesclar.
                    </span>
                  </div>
                )}
                {nearLimit && (
                  <div class="mb-2 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    <span>
                      Você está perto do limite de {MERGE_MAX_MB} MB. Ainda dá pra
                      mesclar, mas evite adicionar bancos muito grandes.
                    </span>
                  </div>
                )}
                <ol class="space-y-1">
                  {selectedBanks.map((b, idx) => (
                    <li
                      key={b.id}
                      class="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                    >
                      <span class="grid size-5 shrink-0 place-items-center rounded bg-primary/10 text-[11px] font-bold text-primary">
                        {idx + 1}
                      </span>
                      <span class="min-w-0 flex-1 truncate">{b.name}</span>
                      <span class="shrink-0 text-[11px] text-muted-foreground">
                        {b.questionCount} q
                      </span>
                      <div class="flex shrink-0 items-center">
                        <button
                          type="button"
                          onClick={() => move(idx, -1)}
                          disabled={idx === 0}
                          class="grid size-6 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30"
                          aria-label="Mover pra cima"
                          title="Mover pra cima"
                        >
                          <ArrowUp className="size-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(idx, 1)}
                          disabled={idx === selectedBanks.length - 1}
                          class="grid size-6 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30"
                          aria-label="Mover pra baixo"
                          title="Mover pra baixo"
                        >
                          <ArrowDown className="size-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggle(b.id)}
                          class="grid size-6 place-items-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          aria-label="Remover"
                          title="Remover da mesclagem"
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {err && <p class="text-xs text-destructive">{err}</p>}
          </div>

          <div class="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
            <span class="text-[11px] text-muted-foreground">
              Originais ficam intactos · duplicadas removidas
            </span>
            <div class="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Layers className="size-3.5" />
                )}
                Mesclar
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function RenameFolderModal({
  folder,
  onClose
}: {
  folder: FolderRow;
  onClose: () => void;
}) {
  const [name, setName] = useState(folder.name);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Foco + seleciona TODO o nome atual ao abrir, pra aluno digitar e
  // substituir direto. Idêntico ao NewFolderModal mas seleciona em vez de
  // só posicionar o caret. [[feedback Karol 2026-05-24]]
  useEffect(() => {
    const handle = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(handle);
  }, []);

  function onOverlayKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.length === 1) {
      e.stopPropagation();
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) {
      setErr("Dê um nome para a pasta.");
      return;
    }
    setSubmitting(true);
    try {
      await call(
        `/api/student/bank-folders?id=${encodeURIComponent(folder.id)}`,
        {
          method: "PATCH",
          body: JSON.stringify({ name: name.trim() })
        }
      );
      await loadFolders({ silent: true });
      onClose();
    } catch (err) {
      setErr(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={onOverlayKeyDown}
    >
      <div class="w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-xl">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-base font-bold">Renomear pasta</h2>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <form onSubmit={submit} class="space-y-3">
          <input
            ref={inputRef}
            type="text"
            required
            maxLength={80}
            value={name}
            onInput={(e) =>
              setName((e.target as HTMLInputElement).value)
            }
            aria-label="Nome da pasta"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          />
          {err && <p class="text-xs text-destructive">{err}</p>}
          <div class="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {submitting && <Loader2 className="size-3.5 animate-spin" />}
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =====================================================================
// Context menu builders (botão direito)
// =====================================================================

/** Itens do menu de contexto pra uma PASTA (mosaico ou árvore). Recebe
 *  os callbacks que o componente caller já tem em scope (states locais).
 *  Compartilhar ainda em backlog — [[project_share_folder_hierarchy]]. */
function folderContextMenuItems(p: {
  name: string;
  onSelect: () => void;
  onCreateSub: () => void;
  onRename: () => void;
  onDelete: () => void;
}): ContextMenuEntry[] {
  return [
    {
      label: "Abrir pasta",
      icon: <Eye className="size-3.5" />,
      onSelect: p.onSelect
    },
    {
      label: "Nova subpasta aqui",
      icon: <FolderPlus className="size-3.5" />,
      onSelect: p.onCreateSub
    },
    { separator: true },
    {
      label: "Renomear",
      icon: <Edit3 className="size-3.5" />,
      onSelect: p.onRename
    },
    {
      label: "Compartilhar pasta",
      icon: <Share2 className="size-3.5" />,
      disabled: true,
      hint: "Em breve — preserva subpastas e bancos no destinatário",
      onSelect: () => undefined
    },
    { separator: true },
    {
      label: "Apagar pasta",
      icon: <Trash2 className="size-3.5" />,
      danger: true,
      onSelect: p.onDelete
    }
  ];
}

/** Itens do menu de contexto pra um BANCO. Remoção ativa pra:
 *  - cópia da biblioteca (re-importável)
 *  - banco compartilhado via grant (re-receptível)
 *  - banco "owned" puro (upload, gerado de caderno) — apaga em definitivo
 *  Banco de disciplina/offering segue desabilitado (sai com o professor). */
function bankContextMenuItems(p: {
  bank: BankRow;
  onOpen: () => void;
  onMoveTo: (folderId: string | null) => void;
  onRemoveFromAcervo: () => void;
}): ContextMenuEntry[] {
  const canRemove = p.bank.canRemoveFromAcervo === true;
  const isLibrary = !!p.bank.importedFromLibraryBankId;
  const ownedPure =
    (p.bank.sources?.some((s) => s.kind === "owned") ?? false) && !isLibrary;
  return [
    {
      label: "Abrir banco",
      icon: <ExternalLink className="size-3.5" />,
      onSelect: p.onOpen
    },
    {
      label: "Mover para…",
      icon: <FolderInput className="size-3.5" />,
      // Submenu lazy: lê foldersData no momento do click pra refletir pastas
      // recém-criadas / renomeadas.
      submenu: () => buildBankMoveSubmenu(p.bank, p.onMoveTo)
    },
    { separator: true },
    {
      label: "Renomear",
      icon: <Edit3 className="size-3.5" />,
      disabled: true,
      hint: "Em breve"
    },
    {
      label: "Compartilhar",
      icon: <Share2 className="size-3.5" />,
      disabled: true,
      hint: "Em breve"
    },
    { separator: true },
    {
      label: ownedPure
        ? "Apagar banco"
        : isLibrary
          ? "Remover do meu acervo"
          : "Remover do acervo",
      icon: <Trash2 className="size-3.5" />,
      danger: true,
      disabled: !canRemove,
      hint: !canRemove
        ? "Este banco vem de uma disciplina — fala com seu professor"
        : ownedPure
          ? "DELETA o banco em definitivo. Sem volta."
          : isLibrary
            ? "Tira só da sua lista. Pode reimportar depois pela Biblioteca."
            : "Tira só da sua lista. O dono pode te enviar de novo.",
      onSelect: p.onRemoveFromAcervo
    }
  ];
}

/** Constrói os itens do submenu "Mover para…" listando todas as pastas (com
 *  indentação por hierarquia) + opção de mover pra raiz. A pasta atual aparece
 *  desabilitada com hint "(atual)". */
function buildBankMoveSubmenu(
  bank: BankRow,
  onMoveTo: (folderId: string | null) => void
): ContextMenuEntry[] {
  const folders = foldersData.value;
  const tree = buildTree(folders);
  const entries: ContextMenuEntry[] = [];
  const currentId = bank.folderId ?? null;

  entries.push({
    label: "Sem pasta (raiz)",
    icon: <Home className="size-3.5" />,
    disabled: currentId === null,
    hint: currentId === null ? "Banco já está na raiz" : undefined,
    onSelect: () => onMoveTo(null)
  });
  entries.push({ separator: true });

  function walk(node: TreeNode) {
    const label = `${"  ".repeat(node.depth)}${node.name}`;
    entries.push({
      label,
      icon: <FolderClosed className="size-3.5 text-amber-500" />,
      disabled: currentId === node.id,
      hint: currentId === node.id ? "Banco já está aqui" : undefined,
      onSelect: () => onMoveTo(node.id)
    });
    for (const child of node.children) walk(child);
  }
  for (const root of tree) walk(root);

  if (folders.length === 0) {
    entries.push({
      label: "Nenhuma pasta criada ainda",
      disabled: true,
      hint: "Crie pastas pelo menu lateral primeiro"
    });
  }
  return entries;
}

// Suppress unused import noise (used via JSX dynamic icon)
void Folder;
void computed;
